import { useRef, useState } from 'react';

import { AppIcon, REMOTE_PERMISSIONS } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useHasPermission } from '@backrs_ui/grow-ui/src/hooks/useHasPermission';
import { useOperatorInstitutionSwitch } from '@backrs_ui/grow-ui/src/hooks/useOperatorInstitutionSwitch';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { useGetOperatorContextQuery } from '@backrs_ui/grow-ui/src/redux/institutionAdmin';
import {
  PLATFORM_OPERATOR_CONTEXT,
  selectOperatingAsRootId,
} from '@backrs_ui/grow-ui/src/redux/operatorContext';

import {
  Box,
  Chip,
  ClickAwayListener,
  Grow,
  MenuItem,
  Paper,
  Popper,
  type SxProps,
  type Theme,
  Typography,
} from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';

const strings = appStrings.modules.coachingDashboard.institutionAdmin.operatingAs;

type OperatingAsChipProps = Readonly<{
  id?: string;
}>;

export const OperatingAsChip = ({ id = 'operating-as-chip' }: OperatingAsChipProps) => {
  const canAccessOperatorFleet = useHasPermission(REMOTE_PERMISSIONS.ACCESS_OPERATOR_FLEET);
  const rootId = useAppSelector(selectOperatingAsRootId);
  const isOperatingAs = canAccessOperatorFleet && rootId !== null;
  // Named by the api, not by the selection, so a reload still knows the operator.
  const context = useGetOperatorContextQuery(isOperatingAs ? undefined : skipToken);
  const onSwitch = useOperatorInstitutionSwitch();
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  if (!isOperatingAs) return null;

  const name = context.data?.root?.name ?? null;

  return (
    <ClickAwayListener onClickAway={() => setIsOpen(false)}>
      <Box sx={anchorSx}>
        <Box ref={anchorRef}>
          <Chip
            id={id}
            variant="tag"
            size="sm"
            label={name === null ? strings.labelUnnamed : strings.label(name)}
            onClick={() => setIsOpen((open) => !open)}
            sx={chipSx}
          />
        </Box>
        <Popper open={isOpen} anchorEl={anchorRef.current} placement="bottom-start" transition>
          {({ TransitionProps }) => (
            <Grow {...TransitionProps} timeout={200} style={{ transformOrigin: 'top' }}>
              <Paper elevation={2} sx={popupPaperSx}>
                <Box sx={popupMenuSx}>
                  <MenuItem
                    id={`${id}-exit`}
                    sx={popupItemSx}
                    onClick={() => {
                      setIsOpen(false);
                      onSwitch(PLATFORM_OPERATOR_CONTEXT);
                    }}
                  >
                    <Box sx={popupItemContentSx}>
                      <AppIcon
                        iconName="LogOut01"
                        diameter="20px"
                        color="Colors/Foreground/fg-quaternary"
                      />
                      <Typography variant="text-sm-semibold" sx={popupItemTextSx}>
                        {strings.exit}
                      </Typography>
                    </Box>
                  </MenuItem>
                </Box>
              </Paper>
            </Grow>
          )}
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

const anchorSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
};

const chipSx: SxProps<Theme> = ({ backrs }) => ({
  width: 'fit-content',
  flexShrink: 0,
  cursor: 'pointer',
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-warning-secondary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-warning-primary'],
  '& .MuiChip-label': {
    color: backrs.colors['Colors/Text/text-warning-primary'],
  },
});

const popupPaperSx: SxProps<Theme> = ({ backrs }) => ({
  bgcolor: backrs.colors['Colors/Background/bg-primary'],
  borderRadius: backrs.radius['radius-xl'],
  marginTop: backrs.spacing['spacing-xs'],
});

const popupMenuSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  minWidth: '180px',
  padding: `${backrs.spacing['spacing-sm']} 0px`,
  flexDirection: 'column',
  alignItems: 'flex-start',
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
  boxShadow: backrs.shadows['shadow-lg'],
});

const popupItemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  padding: `0px ${backrs.spacing['spacing-sm']}`,
  alignItems: 'center',
  alignSelf: 'stretch',
  border: `1px solid ${backrs.colors['Colors/Base/Transparent']}`,
  '&:hover': {
    backgroundColor: backrs.colors['Colors/Background/bg-tertiary'],
    border: `1px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
    borderRadius: backrs.radius['radius-xs'],
  },
});

const popupItemContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  padding: backrs.spacing['spacing-md'],
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  flex: '1 0 0',
});

const popupItemTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
  lineHeight: '20px',
});
